import { useState, useEffect } from 'react';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RightPanel } from '../components/ui/right-panel';
import { ChainVisual } from '../components/ui/chain-visual';
import { PageToolbar, PageKpiStrip, PageContext, GuidedAction, CriticalityBadge, SortableHeader, StaticHeader, Td, Tr, DataTableWrapper, TableHead, ResultsBar, PageShell, SectionTabs, SECTIONS } from '../components/enterprise';
import { CONTROLS } from '../lib/design-tokens';

import DecisionRow from '../components/DecisionRow';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Box, 
  Plus,
  Trash2,
  Server,
  Users,
  HardDrive,
  Cloud,
  Database,
  Network,
  Loader2,
  Search,
  Filter,
  Info,
  ChevronRight,
  ChevronLeft,
  Check,
  ArrowUpDown,
  ShieldAlert,
  AlertTriangle,
  ArrowRight,
  X,
  Monitor
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { ContextualTip } from '../components/ContextualTip';
import { PageError } from '../components/PageError';
import { Link, useNavigate } from 'react-router-dom';

const ASSET_TYPES = [
  { value: 'person', label: 'Persoon', icon: Users, color: 'bg-blue-50 text-blue-600', description: 'Medewerkers, contractors, beheerders met toegang tot systemen' },
  { value: 'software', label: 'Software', icon: Server, color: 'bg-purple-50 text-purple-600', description: 'Applicaties, besturingssystemen, licenties' },
  { value: 'hardware', label: 'Hardware', icon: HardDrive, color: 'bg-orange-50 text-orange-600', description: 'Servers, laptops, mobiele apparaten, randapparatuur' },
  { value: 'service', label: 'Service', icon: Cloud, color: 'bg-green-50 text-green-600', description: 'Cloud services, SaaS, externe diensten' },
  { value: 'data', label: 'Data', icon: Database, color: 'bg-red-50 text-red-600', description: 'Databases, bestanden, klantgegevens, intellectueel eigendom' },
  { value: 'network', label: 'Netwerk', icon: Network, color: 'bg-cyan-50 text-cyan-600', description: 'Routers, switches, firewalls, VPN-verbindingen' },
];

const CRITICALITY_LEVELS = [
  { value: 'low', label: 'Laag', color: 'border-green-200 bg-green-50 text-green-700' },
  { value: 'medium', label: 'Medium', color: 'border-yellow-200 bg-yellow-50 text-yellow-700' },
  { value: 'high', label: 'Hoog', color: 'border-orange-200 bg-orange-50 text-orange-700' },
  { value: 'critical', label: 'Kritiek', color: 'border-red-200 bg-red-50 text-red-700' },
];

const AssetsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCriticality, setFilterCriticality] = useState('all');
  const [filterVuln, setFilterVuln] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [assetSort, setAssetSort] = useState({ field: 'name', dir: 'asc' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [chainData, setChainData] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    asset_type: 'software',
    description: '',
    criticality: 'medium',
    owner: '',
    location: '',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [filterType, filterCriticality]);

  const fetchData = async () => {
    setFetchError(false);
    try {
      const statsRes = await api.get('/ggrc/customer-assets/stats');
      setStats(statsRes.data);
      await fetchAssets();
    } catch (error) {
      setFetchError(true);
      toast.error('Uw gegevens konden niet worden geladen. Ververs de pagina en probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('asset_type', filterType);
      if (filterCriticality !== 'all') params.append('criticality', filterCriticality);
      
      const response = await api.get(`/ggrc/customer-assets?${params}`);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const handleCreateAsset = async () => {
    if (!formData.name || !formData.asset_type) {
      toast.error('Vul minimaal naam en type in');
      return;
    }

    setSaving(true);
    try {
      await api.post('/ggrc/customer-assets', {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('Asset aangemaakt');
      setShowCreateModal(false);
      resetForm();
      fetchAssets();
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het systeem kon niet aanmaken. Controleer uw invoer en probeer het opnieuw.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAsset = async () => {
    if (!selectedAsset) return;

    setSaving(true);
    try {
      await api.patch(`/ggrc/customer-assets/${selectedAsset.id}`, {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('Asset bijgewerkt');
      setShowEditModal(false);
      fetchAssets();
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De wijziging kon niet worden opgeslagen. Probeer het later opnieuw.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Weet u zeker dat u dit asset wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/ggrc/customer-assets/${assetId}`);
      toast.success('Asset verwijderd');
      fetchAssets();
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het item kon niet worden verwijderd. Probeer het later opnieuw.'));
    }
  };

  const openAssetDetail = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name,
      asset_type: asset.asset_type,
      description: asset.description || '',
      criticality: asset.criticality,
      owner: asset.owner || '',
      location: asset.location || '',
      tags: asset.tags?.join(', ') || ''
    });
    setShowDetailPanel(true);
    fetchChainData(asset.id);
  };

  const fetchChainData = async (assetId) => {
    setChainLoading(true);
    try {
      const res = await api.get(`/ggrc/chain/asset/${assetId}`);
      setChainData(res.data);
    } catch {
      setChainData(null);
    } finally {
      setChainLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      asset_type: 'software',
      description: '',
      criticality: 'medium',
      owner: '',
      location: '',
      tags: ''
    });
  };

  const getTypeInfo = (type) => ASSET_TYPES.find(t => t.value === type) || ASSET_TYPES[1];
  const getCriticalityInfo = (crit) => CRITICALITY_LEVELS.find(c => c.value === crit) || CRITICALITY_LEVELS[1];

  // Filter and sort assets
  const critOrder = { critical: 4, high: 3, medium: 2, low: 1 };
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = !searchQuery || [asset.name, asset.description, asset.owner, ...(asset.tags || [])]
      .some(f => (f || '').toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || asset.asset_type === filterType;
    const matchesCrit = filterCriticality === 'all' || asset.criticality === filterCriticality;
    const matchesVuln = filterVuln === 'all' || (filterVuln === 'with' ? (asset.vulnerability_count || 0) > 0 : (asset.vulnerability_count || 0) === 0);
    return matchesSearch && matchesType && matchesCrit && matchesVuln;
  }).sort((a, b) => {
    const { field, dir } = assetSort;
    let cmp = 0;
    if (field === 'name') cmp = (a.name || '').localeCompare(b.name || '');
    else if (field === 'type') cmp = (a.asset_type || '').localeCompare(b.asset_type || '');
    else if (field === 'criticality') cmp = (critOrder[a.criticality] || 0) - (critOrder[b.criticality] || 0);
    else if (field === 'owner') cmp = (a.owner || '').localeCompare(b.owner || '');
    return dir === 'desc' ? -cmp : cmp;
  });

  const toggleSort = (field) => {
    setAssetSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };

  if (loading) {
    return <PageShell section="risicos" testId="assets-page" loading />;
  }

  if (fetchError) {
    return (
      <PageShell section="risicos" testId="assets-page">
        <div className="px-6 py-10">
          <PageError onRetry={() => { setLoading(true); fetchData(); }} />
        </div>
      </PageShell>
    );
  }

  // Human-readable helpers
  const getAssetDescription = (asset) => {
    const critMap = { critical: 'Zeer belangrijk systeem', high: 'Belangrijk systeem', medium: 'Ondersteunend systeem', low: 'Minder kritiek systeem' };
    return critMap[asset.criticality] || 'Systeem';
  };

  return (
    <PageShell section="risicos" testId="assets-page">
        <div className="flex transition-all">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Page Toolbar */}
            <PageToolbar
              title="Systemen"
              subtitle="Registreer en beheer de systemen, processen en diensten van je organisatie."
              count={assets.length}
              onAdd={() => { resetForm(); setWizardStep(0); setShowCreateModal(true); }}
              addLabel="Systeem toevoegen"
              testId="assets"
            />

            {/* KPI Strip */}
            {assets.length > 0 && stats && (
              <PageKpiStrip
                testId="asset-kpi"
                items={[
                  { label: 'Totaal', value: stats.total || assets.length, color: 'text-neutral-800', sub: 'geregistreerd' },
                  { label: 'Kritiek', value: stats.critical || assets.filter(a => a.criticality === 'critical').length, color: 'text-red-600', sub: 'hoge prioriteit' },
                  { label: 'Met kwetsbaarheden', value: assets.filter(a => (a.vulnerability_count || 0) > 0).length, color: 'text-amber-600', sub: 'vereist aandacht' },
                  { label: 'Zonder eigenaar', value: assets.filter(a => !a.owner).length, color: 'text-orange-600', sub: 'niet toegewezen' },
                ]}
              />
            )}

            {/* Page Context + Guided Action */}
            {assets.length > 0 && (
              <>
                <PageContext
                  description="Hier staan de systemen, processen en diensten van je organisatie. Koppel ze aan risico's en kwetsbaarheden voor volledig inzicht."
                  insight={(() => {
                    const noOwner = assets.filter(a => !a.owner).length;
                    const critical = assets.filter(a => a.criticality === 'critical').length;
                    if (noOwner > 0) return `${noOwner} systemen zonder eigenaar`;
                    if (critical > 0) return `${critical} kritieke systemen geregistreerd`;
                    return null;
                  })()}
                  insightType={assets.filter(a => !a.owner).length > 0 ? 'warning' : 'info'}
                  testId="assets-context"
                />
                {assets.filter(a => !a.owner).length > 0 && (
                  <GuidedAction
                    title={`Wijs eigenaren toe aan ${assets.filter(a => !a.owner).length} systemen`}
                    description="Systemen zonder eigenaar worden bij incidenten niet snel genoeg opgepakt. Wijs per systeem een verantwoordelijke aan."
                    actionLabel="Eigenaren toewijzen"
                    onAction={() => {
                      const firstNoOwner = assets.find(a => !a.owner);
                      if (firstNoOwner) openAssetDetail(firstNoOwner);
                    }}
                    duration="1 minuut per systeem"
                    severity="warning"
                    icon={Users}
                    testId="assets-guided-action"
                  />
                )}
              </>
            )}

            {/* Filters */}
            {assets.length > 0 && (
              <div className="px-6 py-3 bg-white border-b border-neutral-100 flex flex-wrap items-center gap-2" data-testid="asset-filters">
                <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                      <input type="text" placeholder="Zoek op naam, eigenaar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-[11px] border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 bg-white" data-testid="asset-search-input" />
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className={`w-36 ${CONTROLS.select} border-neutral-200`} data-testid="asset-type-filter"><SelectValue placeholder="Alle typen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle typen</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="hardware">Hardware</SelectItem>
                        <SelectItem value="cloud">Cloud</SelectItem>
                        <SelectItem value="network">Netwerk</SelectItem>
                        <SelectItem value="data">Data</SelectItem>
                        <SelectItem value="people">Mensen</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterCriticality} onValueChange={setFilterCriticality}>
                      <SelectTrigger className={`w-36 ${CONTROLS.select} border-neutral-200`} data-testid="asset-criticality-filter"><SelectValue placeholder="Alle belangen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle belangen</SelectItem>
                        <SelectItem value="critical">Kritiek</SelectItem>
                        <SelectItem value="high">Hoog</SelectItem>
                        <SelectItem value="medium">Middel</SelectItem>
                        <SelectItem value="low">Laag</SelectItem>
                      </SelectContent>
                    </Select>
                    {filteredAssets.length !== assets.length && (
                      <span className="text-xs text-neutral-400 ml-auto">{filteredAssets.length} resultaten</span>
                    )}
                  </div>
                )}

            {/* Data Table */}
            <div className="bg-white" data-testid="asset-table-container">
                {filteredAssets.length > 0 && (
                  <DataTableWrapper testId="asset-table">
                    <TableHead>
                      <SortableHeader label="Naam" field="name" sortField={assetSort.field} sortDir={assetSort.dir} onSort={toggleSort} />
                      <StaticHeader label="Type" className="w-28" />
                      <SortableHeader label="Belang" field="criticality" sortField={assetSort.field} sortDir={assetSort.dir} onSort={toggleSort} className="w-28" />
                      <StaticHeader label="Eigenaar" className="w-36" />
                      <StaticHeader label="Kwetsb." className="w-20 text-center" />
                      <th className="w-8 px-2" />
                    </TableHead>
                    <tbody>
                      {filteredAssets.map((asset) => {
                        const typeInfo = getTypeInfo(asset.asset_type);
                        const isSelected = selectedAsset?.id === asset.id && showDetailPanel;
                        return (
                          <Tr key={asset.id} active={isSelected} onClick={() => openAssetDetail(asset)} testId={`asset-row-${asset.id}`}>
                            <Td>
                              <div className="flex items-center gap-2.5">
                                <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${typeInfo.color}`}>
                                  <typeInfo.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                </div>
                                <span className="text-sm font-medium text-[#111827] truncate max-w-[240px]">{asset.name}</span>
                              </div>
                            </Td>
                            <Td><span className="text-sm text-[#4B5563]">{typeInfo.label}</span></Td>
                            <Td><CriticalityBadge level={asset.criticality} /></Td>
                            <Td><span className="text-sm text-[#111827] truncate max-w-[120px] block">{asset.owner || <span className="text-[#9CA3AF] italic">Geen</span>}</span></Td>
                            <Td align="center">
                              <span className={`text-sm font-bold ${(asset.vulnerability_count || 0) > 0 ? 'text-[#92400E]' : 'text-[#9CA3AF]'}`}>
                                {asset.vulnerability_count || 0}
                              </span>
                            </Td>
                            <td className="px-2 py-3"><ChevronRight className="h-4 w-4 text-neutral-300" strokeWidth={1.5} /></td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </DataTableWrapper>
                )}

                {/* EMPTY STATE */}
                {assets.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-assets">
                    <div className="h-14 w-14 rounded-xl bg-neutral-100 flex items-center justify-center mb-4">
                      <Monitor className="h-7 w-7 text-neutral-400" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-base font-semibold text-neutral-800">Geen systemen</h2>
                    <p className="text-sm text-neutral-500 mt-1 max-w-sm">Registreer je systemen om ze te koppelen aan risico's en kwetsbaarheden.</p>
                    <Button className={`${CONTROLS.primary} bg-[#111827] text-white mt-5`} onClick={() => { resetForm(); setWizardStep(0); setShowCreateModal(true); }}>
                      <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />Eerste systeem toevoegen
                    </Button>
                  </div>
                )}

                {/* Results count */}
                {filteredAssets.length > 0 && (
                  <ResultsBar shown={filteredAssets.length} total={assets.length} />
                )}
            </div>
          </div>

          {/* RIGHT: Detail Split Panel */}
          {showDetailPanel && selectedAsset && (
            <div className="w-[420px] shrink-0 border-l border-neutral-200 bg-white h-screen sticky top-0 overflow-y-auto" data-testid="asset-detail-split">
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between p-4 border-b border-neutral-200 bg-neutral-50/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`h-6 w-6 rounded flex items-center justify-center ${getTypeInfo(selectedAsset.asset_type).color}`}>
                        {(() => { const I = getTypeInfo(selectedAsset.asset_type).icon; return <I className="h-3 w-3" strokeWidth={1.5} />; })()}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${getCriticalityInfo(selectedAsset.criticality).color}`}>{getCriticalityInfo(selectedAsset.criticality).label}</span>
                    </div>
                    <h2 className="text-sm font-bold text-neutral-900">{selectedAsset.name}</h2>
                  </div>
                  <button onClick={() => { setShowDetailPanel(false); setChainData(null); setSelectedAsset(null); }}
                    className="ml-2 h-7 w-7 rounded-md border border-neutral-200 flex items-center justify-center hover:bg-neutral-100 shrink-0" data-testid="asset-split-close">
                    <X className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {/* Edit form — compact */}
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-neutral-500">Naam</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-8 text-xs" data-testid="edit-asset-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Type</Label>
                        <Select value={formData.asset_type} onValueChange={(v) => setFormData({ ...formData, asset_type: v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid="edit-asset-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Belang</Label>
                        <Select value={formData.criticality} onValueChange={(v) => setFormData({ ...formData, criticality: v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid="edit-asset-criticality-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{CRITICALITY_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Eigenaar</Label>
                        <Input value={formData.owner} onChange={(e) => setFormData({ ...formData, owner: e.target.value })} className="h-8 text-xs" data-testid="edit-asset-owner-input" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Locatie</Label>
                        <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="h-8 text-xs" data-testid="edit-asset-location-input" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-neutral-500">Tags</Label>
                      <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className="h-8 text-xs" placeholder="komma-gescheiden" data-testid="edit-asset-tags-input" />
                    </div>
                  </div>
                  {/* Chain data */}
                  {chainData && !chainLoading && (
                    <div className="pt-3 border-t border-neutral-100">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Gekoppeld</p>
                      <div className="grid grid-cols-2 gap-2" data-testid="asset-chain-overview">
                        {[
                          { label: 'Kwetsbaarheden', count: chainData.vulnerabilities?.length || 0, href: '/vulnerabilities', color: 'text-amber-500' },
                          { label: 'Dreigingen', count: chainData.threats?.length || 0, href: '/threats', color: 'text-red-500' },
                          { label: "Risico's", count: chainData.risks?.length || 0, href: '/risks', color: 'text-orange-500' },
                          { label: 'Maatregelen', count: chainData.measures?.length || 0, href: '/measures', color: 'text-emerald-500' },
                        ].map((c, i) => (
                          <Link key={i} to={c.href} className="p-2 rounded-lg border border-neutral-200 hover:border-neutral-300 transition-all">
                            <p className={`text-lg font-bold ${c.color}`}>{c.count}</p>
                            <p className="text-[9px] text-neutral-400">{c.label}</p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-neutral-200 flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                    onClick={() => { handleDeleteAsset(selectedAsset.id); setShowDetailPanel(false); }} data-testid="delete-asset-button">Verwijderen</Button>
                  <div className="flex-1" />
                  <Button size="sm" className="bg-black text-white text-[10px] h-7 px-3" onClick={handleUpdateAsset} disabled={saving} data-testid="update-asset-submit">
                    {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Opslaan
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuw Asset</DialogTitle>
            <DialogDescription>
              {wizardStep === 0 && 'Stap 1 van 3: kies het type asset'}
              {wizardStep === 1 && 'Stap 2 van 3: vul de details in'}
              {wizardStep === 2 && 'Stap 3 van 3: tags en locatie'}
            </DialogDescription>
          </DialogHeader>

          {/* Wizard Progress */}
          <div className="flex items-center gap-1 px-1" data-testid="wizard-progress">
            {['Type', 'Details', 'Tags'].map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div className={`flex items-center gap-1.5 flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                  i < wizardStep ? 'text-green-700 bg-green-50' : i === wizardStep ? 'text-black bg-neutral-100' : 'text-neutral-400'
                }`}>
                  <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                    i < wizardStep ? 'bg-green-600 text-white' : i === wizardStep ? 'bg-black text-white' : 'bg-neutral-200 text-neutral-500'
                  }`}>
                    {i < wizardStep ? <Check className="h-3 w-3" strokeWidth={2} /> : i + 1}
                  </div>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {i < 2 && <div className={`w-4 h-px shrink-0 ${i < wizardStep ? 'bg-green-300' : 'bg-neutral-200'}`} />}
              </div>
            ))}
          </div>
          
          <div className="py-2">
            {/* Step 0: Type */}
            {wizardStep === 0 && (
              <div className="space-y-2" data-testid="wizard-step-type">
                <p className="text-xs text-neutral-500 mb-3">Selecteer het type asset dat u wilt registreren:</p>
                <div className="grid grid-cols-2 gap-3">
                  {ASSET_TYPES.map((type) => {
                    const Icon = type.icon;
                    const selected = formData.asset_type === type.value;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setFormData({ ...formData, asset_type: type.value })}
                        className={`p-3 rounded-lg border text-left transition-all hover:shadow-sm ${
                          selected ? 'border-black bg-neutral-50 ring-1 ring-black' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid={`wizard-type-${type.value}`}
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${type.color}`}>
                          <Icon className="h-4 w-4" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm font-medium text-black">{type.label}</p>
                        <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 1: Details */}
            {wizardStep === 1 && (
              <div className="space-y-4" data-testid="wizard-step-details">
                <div className="space-y-3">
                  <Label htmlFor="name">Naam *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="bijv. Productie Server"
                    data-testid="asset-name-input"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="description">Beschrijving</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Beschrijf het asset en zijn functie..."
                    rows={2}
                    data-testid="asset-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label>Kritiekheid</Label>
                    <Select 
                      value={formData.criticality} 
                      onValueChange={(v) => setFormData({ ...formData, criticality: v })}
                    >
                      <SelectTrigger data-testid="asset-criticality-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CRITICALITY_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="owner">Eigenaar</Label>
                    <Input
                      id="owner"
                      value={formData.owner}
                      onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                      placeholder="bijv. IT Afdeling"
                      data-testid="asset-owner-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Tags & Location */}
            {wizardStep === 2 && (
              <div className="space-y-4" data-testid="wizard-step-tags">
                <div className="space-y-3">
                  <Label htmlFor="location">Locatie</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="bijv. Datacenter NL, Hoofdkantoor"
                    data-testid="asset-location-input"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="tags">Tags (komma-gescheiden)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="bijv. productie, kritiek, extern"
                    data-testid="asset-tags-input"
                  />
                  <p className="text-[10px] text-neutral-400">Tags helpen u om assets te groeperen en snel terug te vinden</p>
                </div>

                {/* Summary */}
                <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 space-y-2.5">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Samenvatting</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-black">{getTypeInfo(formData.asset_type).label}</span>
                    <span className="text-neutral-500">Naam:</span>
                    <span className="font-medium text-black">{formData.name || '-'}</span>
                    <span className="text-neutral-500">Kritiekheid:</span>
                    <span className="font-medium text-black">{getCriticalityInfo(formData.criticality).label}</span>
                    {formData.owner && <><span className="text-neutral-500">Eigenaar:</span><span className="font-medium text-black">{formData.owner}</span></>}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => wizardStep === 0 ? setShowCreateModal(false) : setWizardStep(wizardStep - 1)}
              data-testid="wizard-back-button"
            >
              {wizardStep === 0 ? 'Annuleren' : <><ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />Vorige</>}
            </Button>
            {wizardStep < 2 ? (
              <Button 
                className="bg-black text-white hover:bg-neutral-800"
                onClick={() => {
                  if (wizardStep === 1 && !formData.name) {
                    toast.error('Vul minimaal een naam in');
                    return;
                  }
                  setWizardStep(wizardStep + 1);
                }}
                data-testid="wizard-next-button"
              >
                Volgende<ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
              </Button>
            ) : (
              <Button 
                className="bg-black text-white hover:bg-neutral-800"
                onClick={handleCreateAsset}
                disabled={saving}
                data-testid="create-asset-submit"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" strokeWidth={1.5} />}
                Aanmaken
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Right Panel */}
      <RightPanel
        open={showDetailPanel}
        onClose={() => { setShowDetailPanel(false); setChainData(null); setSelectedAsset(null); }}
        title={selectedAsset?.name || 'Asset'}
        subtitle={selectedAsset ? `${getTypeInfo(selectedAsset.asset_type).label} · ${getCriticalityInfo(selectedAsset.criticality).label}` : ''}
        testId="asset-detail-panel"
        chain={
          chainLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-neutral-400" strokeWidth={1.5} />
              <span className="text-xs text-neutral-400">Keten laden...</span>
            </div>
          ) : (
            <ChainVisual
              activeStep="asset"
              data={{
                asset: selectedAsset?.name || '',
                vulnerability: chainData?.vulnerabilities?.length ? `${chainData.vulnerabilities.length} kwetsb.` : '',
                threat: chainData?.threats?.length ? `${chainData.threats.length} dreig.` : '',
                risk: chainData?.risks?.length ? `${chainData.risks.length} risico's` : '',
                measure: chainData?.measures?.length ? `${chainData.measures.length} maatr.` : '',
              }}
              testId="asset-chain-visual"
            />
          )
        }
        actions={
          <>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => { handleDeleteAsset(selectedAsset?.id); setShowDetailPanel(false); setChainData(null); }}
              data-testid="delete-asset-button"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              Verwijderen
            </Button>
            <div className="flex-1" />
            <Button
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleUpdateAsset}
              disabled={saving}
              data-testid="update-asset-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Opslaan'}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {/* DecisionRows — Guided Autonomy */}
          <div className="space-y-1" data-testid="asset-decisions">
            <DecisionRow
              label="Kritiekheid"
              value={getCriticalityInfo(selectedAsset?.criticality).label}
              type={selectedAsset?.criticality ? 'auto' : 'recommended'}
              rationale={selectedAsset?.criticality
                ? `Ingesteld als ${getCriticalityInfo(selectedAsset.criticality).label}`
                : 'Stel de kritiekheid in zodat risico\'s correct geprioriteerd worden'}
              confirmed={!!selectedAsset?.criticality}
              onEdit={() => {}}
            />
            <DecisionRow
              label="Eigenaar"
              value={selectedAsset?.owner || formData.owner || 'Geen eigenaar toegewezen'}
              type={(selectedAsset?.owner || formData.owner) ? 'auto' : 'required'}
              rationale={(selectedAsset?.owner || formData.owner)
                ? 'Toegewezen eigenaar is verantwoordelijk voor dit systeem'
                : 'Zonder eigenaar worden kwetsbaarheden van dit systeem niet opgepakt'}
              confirmed={!!(selectedAsset?.owner || formData.owner)}
            />
          </div>

          {/* Bewerk formulier */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-xs font-medium text-neutral-500">Naam</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="edit-asset-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-neutral-500">Type</Label>
                <Select
                  value={formData.asset_type}
                  onValueChange={(v) => setFormData({ ...formData, asset_type: v })}
                >
                  <SelectTrigger data-testid="edit-asset-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description" className="text-xs font-medium text-neutral-500">Beschrijving</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="edit-asset-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-neutral-500">Kritiekheid</Label>
                <Select
                  value={formData.criticality}
                  onValueChange={(v) => setFormData({ ...formData, criticality: v })}
                >
                  <SelectTrigger data-testid="edit-asset-criticality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CRITICALITY_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-owner" className="text-xs font-medium text-neutral-500">Eigenaar</Label>
                <Input
                  id="edit-owner"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  data-testid="edit-asset-owner-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-location" className="text-xs font-medium text-neutral-500">Locatie</Label>
                <Input
                  id="edit-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="edit-asset-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tags" className="text-xs font-medium text-neutral-500">Tags</Label>
                <Input
                  id="edit-tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  data-testid="edit-asset-tags-input"
                />
              </div>
            </div>
          </div>

          {/* Ketenrelaties */}
          {chainData && !chainLoading && (
            <div className="pt-4 border-t border-neutral-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-3">Gekoppelde objecten</p>
              <div className="grid grid-cols-2 gap-3" data-testid="asset-chain-overview">
                <Link to="/vulnerabilities" className="p-3 rounded-lg border border-neutral-200 hover:border-orange-200 hover:bg-orange-50/50 transition-all group">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className="h-3.5 w-3.5 text-orange-500" strokeWidth={1.5} />
                    <span className="text-[10px] font-bold uppercase text-neutral-400 group-hover:text-orange-500">Zwakke plekken</span>
                  </div>
                  <p className="text-lg font-bold text-black">{chainData.vulnerabilities?.length || 0}</p>
                  {chainData.vulnerabilities?.slice(0, 2).map((v, i) => (
                    <p key={i} className="text-[10px] text-neutral-500 truncate">{v.title}</p>
                  ))}
                </Link>
                <Link to="/threats" className="p-3 rounded-lg border border-neutral-200 hover:border-red-200 hover:bg-red-50/50 transition-all group">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" strokeWidth={1.5} />
                    <span className="text-[10px] font-bold uppercase text-neutral-400 group-hover:text-red-500">Aanvallen</span>
                  </div>
                  <p className="text-lg font-bold text-black">{chainData.threats?.length || 0}</p>
                  {chainData.threats?.slice(0, 2).map((t, i) => (
                    <p key={i} className="text-[10px] text-neutral-500 truncate">{t.title}</p>
                  ))}
                </Link>
                <Link to="/risks" className="p-3 rounded-lg border border-neutral-200 hover:border-amber-200 hover:bg-amber-50/50 transition-all group">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" strokeWidth={1.5} />
                    <span className="text-[10px] font-bold uppercase text-neutral-400 group-hover:text-amber-500">Risico's</span>
                  </div>
                  <p className="text-lg font-bold text-black">{chainData.risks?.length || 0}</p>
                  {chainData.risks?.slice(0, 2).map((r, i) => (
                    <p key={i} className="text-[10px] text-neutral-500 truncate">{r.title}</p>
                  ))}
                </Link>
                <Link to="/measures" className="p-3 rounded-lg border border-neutral-200 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all group">
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={1.5} />
                    <span className="text-[10px] font-bold uppercase text-neutral-400 group-hover:text-emerald-500">Maatregelen</span>
                  </div>
                  <p className="text-lg font-bold text-black">{chainData.measures?.length || 0}</p>
                  {chainData.measures?.slice(0, 2).map((m, i) => (
                    <p key={i} className="text-[10px] text-neutral-500 truncate">{m.name}</p>
                  ))}
                </Link>
              </div>
            </div>
          )}
        </div>
      </RightPanel>
    </PageShell>
  );
};

export default AssetsPage;
